import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import ReimbursementFormInner from './ReimbursementFormInner';

import {
  createReimbursement,
  createReimbursementWithFile,
} from '../../api/reimbursements';
import { extractErrorMessage } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';

const EMPTY = {
  title: '',
  description: '',
  amount: '',
  category: 'TRAVEL',
  department: 'HR',
  paymentMode: 'BANK_TRANSFER',
  transactionDate: '',
  projectId: '',
};

/**
 * Modal-based create form for a Reimbursement claim.
 *
 * If the user attaches a file, we send a multipart request; otherwise we
 * send the JSON payload directly. Matches the two backend variants.
 */
export default function ReimbursementForm({ open, onClose, onCreated }) {
  const qc = useQueryClient();
  const { user } = useAuth();

  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setFile(null);
      setErrors({});
    }
  }, [open]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        amount: Number(form.amount),
        category: form.category,
        department: form.department || null,
        paymentMode: form.paymentMode || null,
        transactionDate: form.transactionDate || null,
        projectId: form.projectId ? Number(form.projectId) : null,
      };
      return file
        ? createReimbursementWithFile(payload, file)
        : createReimbursement(payload);
    },
    onSuccess: (created) => {
      toast.success('Reimbursement claim created');
      qc.invalidateQueries({ queryKey: ['reimbursements'] });
      onCreated?.(created);
      onClose?.();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function validate() {
    const next = {};
    if (!form.title.trim()) next.title = 'Title is required';
    if (!form.amount || Number(form.amount) <= 0)
      next.amount = 'Amount must be greater than zero';
    if (!form.category) next.category = 'Category is required';
    if (!form.projectId) next.projectId = 'Project is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function onSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New reimbursement claim"
      description={`Filed by ${user?.username || 'you'}`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={onSubmit}
          >
            Create claim
          </Button>
        </>
      }
    >
      <form
        onSubmit={onSubmit}
        className="grid grid-cols-1 gap-4"
      >
        <ReimbursementFormInner
          form={form}
          update={update}
          errors={errors}
          file={file}
          setFile={setFile}
        />
      </form>
    </Modal>
  );
}
